/**
 * Recordatorio de «Por hacer» en Inicio: cuentas que llevan más de un mes
 * sin cuadrar con el banco. Lógica pura (qué cuentas, desde cuándo y cómo
 * decirlo); el componente pone los textos traducidos.
 */
import { estaEnlazada } from '#components/mobile/accounts/banco';

/** Más de estos días sin cuadrar = aviso. */
export const DIAS_SIN_CUADRAR = 30;
/** Con más cuentas que estas, una sola fila agrupada que lleva a Cuentas. */
export const MAX_FILAS_SIN_CUADRAR = 3;

const DIA_MS = 24 * 60 * 60 * 1000;

export type CuentaCuadre = {
  id: string;
  name: string;
  closed: 0 | 1 | boolean;
  offbudget: 0 | 1 | boolean;
  /** Milisegundos (como texto) del último cuadre; Actual lo guarda al conciliar. */
  last_reconciled: string | null;
  account_id?: string | null;
  account_sync_source?: string | null;
};

export type SinCuadrar = { id: string; nombre: string; dias: number };

/** Hace cuánto, en la unidad más natural: 1 mes, 5 semanas, 2 meses, +1 año. */
export type HaceCuanto =
  | { unidad: 'semanas'; n: number }
  | { unidad: 'meses'; n: number }
  | { unidad: 'anos'; n: number };

/** Medianoche local de una fecha `YYYY-MM-DD`. */
function msFecha(fecha: string): number {
  const [a, m, d] = fecha.split('-').map(Number);
  return new Date(a, m - 1, d).getTime();
}

/**
 * ¿Le toca a esta cuenta el recordatorio? Abierta y, o enlazada al banco, o
 * de uso diario (dentro del presupuesto). Fuera quedan los préstamos y los
 * activos manuales (fuera del presupuesto y sin banco).
 */
export function cuentaCuadrable(
  c: CuentaCuadre,
  prestamos: ReadonlySet<string>,
): boolean {
  if (c.closed || prestamos.has(c.id)) {
    return false;
  }
  return estaEnlazada(c) || !c.offbudget;
}

/**
 * Cuentas que llevan más de `DIAS_SIN_CUADRAR` días sin cuadrar, de la más
 * olvidada a la más reciente. Si nunca se ha cuadrado, cuenta desde su primer
 * movimiento (`primerMovimiento`, `YYYY-MM-DD`); sin movimientos, no avisa.
 */
export function cuentasSinCuadrar(
  cuentas: readonly CuentaCuadre[],
  prestamos: ReadonlySet<string>,
  primerMovimiento: ReadonlyMap<string, string>,
  ahora: number,
): SinCuadrar[] {
  const res: SinCuadrar[] = [];
  for (const c of cuentas) {
    if (!cuentaCuadrable(c, prestamos)) {
      continue;
    }
    const cuadre = Number(c.last_reconciled || NaN);
    const primera = primerMovimiento.get(c.id);
    const desde = Number.isFinite(cuadre)
      ? cuadre
      : primera
        ? msFecha(primera)
        : NaN;
    if (!Number.isFinite(desde)) {
      continue;
    }
    const dias = Math.floor((ahora - desde) / DIA_MS);
    if (dias > DIAS_SIN_CUADRAR) {
      res.push({ id: c.id, nombre: c.name, dias });
    }
  }
  return res.sort(
    (a, b) => b.dias - a.dias || a.nombre.localeCompare(b.nombre),
  );
}

/**
 * Cuentas que no tienen último cuadre: para ellas hace falta la fecha del
 * primer movimiento.
 */
export function idsSinCuadreNunca(
  cuentas: readonly CuentaCuadre[],
  prestamos: ReadonlySet<string>,
): string[] {
  return cuentas
    .filter(
      c =>
        cuentaCuadrable(c, prestamos) &&
        !Number.isFinite(Number(c.last_reconciled || NaN)),
    )
    .map(c => c.id);
}

/**
 * «Hace 1 mes» hasta las 4 semanas y media, luego semanas (5 a 8), luego
 * meses (2 a 11) y, pasado el año, años.
 */
export function haceCuanto(dias: number): HaceCuanto {
  if (dias >= 365) {
    return { unidad: 'anos', n: Math.floor(dias / 365) };
  }
  if (dias >= 60) {
    return { unidad: 'meses', n: Math.floor(dias / 30) };
  }
  const semanas = Math.floor(dias / 7);
  if (semanas >= 5) {
    return { unidad: 'semanas', n: semanas };
  }
  return { unidad: 'meses', n: 1 };
}

/** Una fila por cuenta (hasta 3) o una agrupada si son más. */
export function filasSinCuadrar(
  sinCuadrar: readonly SinCuadrar[],
): { tipo: 'cuenta'; cuenta: SinCuadrar }[] | [{ tipo: 'grupo'; n: number }] {
  if (sinCuadrar.length > MAX_FILAS_SIN_CUADRAR) {
    return [{ tipo: 'grupo', n: sinCuadrar.length }];
  }
  return sinCuadrar.map(cuenta => ({ tipo: 'cuenta', cuenta }));
}
